import { type PropsWithChildren, useEffect, useMemo, useState } from 'react';

import { COOKIE_KEYS } from '@moniebook-web/types';
import {
    CustomerMetricsContext,
    getItem,
    useUserInfo,
} from '@moniebook-web/utils';
import {
    type CxMetricOptions,
    getSDK,
    identifyUser,
    pushData,
    setup,
} from '@web-platform/cx-metrics-browser';

export interface CustomerMetricsProviderProps {
    cxMetricsOptions: CxMetricOptions;
}

export function CustomerMetricsProvider({
    cxMetricsOptions,
    children,
}: Readonly<PropsWithChildren<CustomerMetricsProviderProps>>) {
    const [isReady, setIsReady] = useState(false);
    const token = getItem(COOKIE_KEYS.TOKEN_VARIABLE);
    const { currentBusiness, user } = useUserInfo(Boolean(token));

    // Setup CX Metrics
    useEffect(() => {
        if (isReady) return;

        setup(cxMetricsOptions).then(() => setIsReady(true));
    }, [cxMetricsOptions, isReady]);

    // Identify user
    useEffect(() => {
        if (!currentBusiness || !isReady || !user) return;

        identifyUser({
            customerIdentifier: currentBusiness.staffId,
            user: {
                authToken: token,
                id: currentBusiness.staffId,
                first_name: user.firstName,
                last_name: user.lastName,
                businessId: currentBusiness.id,
                email: user.email,
                business_name: currentBusiness.name,
            },
        });
    }, [currentBusiness, user, token, isReady]);

    const value = useMemo(
        () => ({
            isReady,
            getSDK: isReady ? getSDK : undefined,
            pushData: isReady ? pushData : undefined,
            platform: 'DASHBOARD',
        }),
        [isReady],
    );

    return (
        <CustomerMetricsContext.Provider value={value}>
            {children}
        </CustomerMetricsContext.Provider>
    );
}
